import React from 'react';
import { injectIntl } from 'react-intl';
import { defaultValue, isNotEmpty, stripTags } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';

import DynamicFields, { localizeFields } from '../../components/builder/DynamicFields.js';
import Input from '../../components/builder/Input.js';
import MatrixSelector from '../../components/builder/MatrixSelector.js';
import MediaUploader from '../../components/builder/MediaUploader.js';
import Model from '../../components/builder/Model.js';
import Range from '../../components/builder/Range.js';
import RichEditor from '../../components/builder/RichEditor.js';
import UrlField from '../../components/builder/UrlField.js';

import VideoEmbed from '../../components/front/VideoEmbed.js';

import LeveledHTMLText from '../../components/front/helpers/LeveledHTMLText.js';

type BlockDisplayType = {
	blocks:BlockType[];
	locale:string | null;
}

type BlockType = {
	text?:string;
	image?:string;
	full_image?:string;
	media_url?:string;
	media_url_target?:string;
	caption?:string;
	video?:string;
	percent:number;
	class?:string,
	position:'cl' | 'cr'
}

type BlockEditorType = {
	/**Name of the block field property that will be passed on save and in the model */
	name: string;
	/**When a change is produced in any of the block items the full block is passed */
	change: (name:string,value: object[]) => void;
	/**Object containg a field with the block json info */
	model?:string;
	/**The url where the media will be uploaded */
	auto_url?:string;
	/**The type of media to indicate where to save it */
	auto_type?:string;
};

const SingleImage = ({ src, ...props }) => {
	const link = props?.link ?? false;

	if(link !== false) {
		return (
			<a
				href={link}
				target={props?.target ?? '_blank'}
				rel="noopener noreferrer"
			>
				<img
					src={src}
					width="100%"
					alt={props?.alt ?? ""}
				/>
			</a>
		)
	}

	return (
		<img
			src={src}
			width="100%"
			alt={props?.alt ?? ""}
		/>
	)
};

const ImageWithCaption = (props) => {
	const src = (isNotEmpty(props, 'src')) ? props.src : false;
	if(src === false) return null;

	const caption = (isNotEmpty(props, 'caption')) ? props.caption : false;

	return (
		<figure className={(props?.classes ?? []).join(" ")}>
			<SingleImage
				src={src}
				link={props.link}
				target={props.target}
			/>
			
			{(caption !== false) && (
				<figcaption
					className='caption'
					dangerouslySetInnerHTML={{ __html:caption }}
				/>
			)}
		</figure>
	)
};

const VideoWithCaption = (props) => {
	const src = (isNotEmpty(props, 'src')) ? props.src : false;
	if(src === false) return null;

	const caption = (isNotEmpty(props, 'caption')) ? props.caption : false;

	return (
		<figure className={(props?.classes ?? []).join(" ")}>
			<VideoEmbed
				video_url={src}
			/>
			
			{(caption !== false) && (
				<figcaption
					className='caption'
					dangerouslySetInnerHTML={{ __html:caption }}
				/>
			)}
		</figure>
	)
};

const Block = ({ block, locale, ...props }:{block:BlockType, locale:string}) => {
	const media_and_text = (isNotEmpty(block, 'text')) && (block?.image || block?.video) ? true : false;
	const is_nested = props?.is_nested ?? false;

	const classes = (is_nested === false) ? ['gui_block_content'] : [];
	if(isNotEmpty(block, 'class')) classes.push(block.class);

	if(media_and_text) {
		const blocks:BlockType[] = [
			{ ...block, text:"" },
			{ ...block, image:'', video:'' },
		];

		classes.push('gui_block_content');
		classes.push('both');
		classes.push(`img_${(block.position == 'cr') ? 'right' : 'left'}`);

		return (
			<div
				className={classes.join(" ")}
				style={{
					'--block-img-w-prop':`${defaultValue(block, 'percent', 50)}%`,
				}}
			>
				<BlockDisplay
					blocks={blocks}
					locale={locale}
					is_nested={true}
				/>
			</div>
		)
	}

	if(isNotEmpty(block, 'text')) {
		classes.push('block_text');

		return (
			<LeveledHTMLText
				_class={{ other:classes.join(" ") }}
			>{block.text}</LeveledHTMLText>
		)
	}

	classes.push('block_img');

	if(isNotEmpty(block,'image')) {
		return (
			<ImageWithCaption
				src={block.full_image}
				caption={block.caption}
				link={block.media_url}
				target={block.media_url_target}
				classes={classes}
			/>
		);
	}

	if(isNotEmpty(block, 'video')) {
		classes.push('is_video');

		return (
			<VideoWithCaption
				src={block.video}
				caption={block.caption}
				classes={classes}
			/>
		)
	}

	return null;
};

export const BlockDisplay = (props:BlockDisplayType) => {
	useCSS('comp/text_blocks.css');

	const blocks = localizeFields(props.blocks, props.locale);

	return (
		<>
			{blocks.map((block) => {
				return (
					<Block
						key={`bl_${block.id}`}
						block={block}
						locale={props.locale}
						is_nested={props.is_nested}
					/>
				)
			})}
		</>
	)
};

const BlockField = (props) => {
	const media_and_text = isNotEmpty(props.model, 'text') && (isNotEmpty(props.model, 'image') || isNotEmpty(props.model, 'video'));

	const change = (k, v) => {
		props.change(k, v);
		if((k == 'image' || k == 'video' || k == 'text') && v === '') {
			props.change('percent', "");
		}
	};

	return (
		<Model model={props.model} change={change}>
			<RichEditor
				name="text"
				has_emojis={true}
			>Text</RichEditor>

			<MediaUploader
				auto_url={props.auto_url}
				auto_type={props.auto_type}
				name="media"
				imageField='image'
				videoField='video'
				image={props.model?.full_image}
				video={props.model?.video}
			>Media</MediaUploader>

			{props.model?.image && (
				<UrlField
					name="media_url"
					_class={{ size:60 }}
				>Media URL</UrlField>
			)}

			{(isNotEmpty(props.model, 'video') || isNotEmpty(props.model, 'image')) && (
				<Input
					name="caption"
					_class={{ size:60 }}
				>Media caption</Input>
			)}

			{(media_and_text) && (
				<Range
					name="percent"
					min={10}
					max={90}
					step={10}
					value={defaultValue(props.model, 'percent', 50)}
					_class={{ size:60 }}
				>Media proportion</Range>
			)}

			{(media_and_text) && (
				<MatrixSelector
					name="position"
					value={props?.model?.position ?? 'cl'}
					allow='x_object_binary'
					enable_center={false}
					_default_value='cl'
				>Media position</MatrixSelector>
			)}

			<Input
				name="class"
				_class={{ size:60 }}
			>CSS class</Input>
		</Model>
	)
}

const BlockEditor = (props:BlockEditorType) => {
	useCSS('comp/text_blocks.css');

	const blocks = props?.model != undefined && props.model[props.name] != undefined ? props.model[props.name] : [];
	const dynamic_fields = [{
		type:'html_media',
		label:"Add Block",
		translate:[{
			key:'text',
			type:'richeditor',
		}, {
			key:'image',
			type:'image',
			uploader:{
				auto_url:props.auto_url,
				auto_type:props.auto_type,
			},
		}, {
			key:'video',
			type:'youtube',
		}, {
			key:'media_url',
			type:'url',
		}, {
			key:'caption',
			type:'input',
		}],
		title:(f) => {
			if(isNotEmpty(f,'video')) return f.video;
			return (isNotEmpty(f, 'text')) ? stripTags(f.text, true) : `Empty`;
		},
	}];

	const onChange = (fields) => {
		if(props.change == undefined) return;
		props.change(props.name, fields);
	};

	return (
		<DynamicFields
			onChange={onChange}
			section_title={'Block editor'}
			field_types={dynamic_fields}
			has_translation={true}
			locales={props?.locales ?? []}
			fields={blocks}
		>
			<BlockField
				auto_url={props.auto_url}
				auto_type={props.auto_type}
			/>
		</DynamicFields>
	)
};

export default injectIntl(BlockEditor);